import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { db } from '../../../lib/payments/db';
import { CHEMINS, ficheDuCentre } from '../../../lib/navigation/chemins';
import { enregistrerJournee, lireClasses, lireJournees } from '../../../lib/jpa/gateway.supabase';
import {
  bruxellesVersIso,
  centimesSaisis,
  dateLimiteParDefaut,
  isoVersBruxelles,
  moitieExacte,
  telephoneDuCentreChoisi,
  type ClasseDuCentre,
} from '../../../lib/jpa/regles';

/*
  Programmer une journée pédagogique, ou la modifier.

  Une journée par date : le 5 et le 6 novembre sont deux journées, chacune
  avec sa date limite (VALIDÉ ÉCLOSION, 19/10/2026). Les quatre prix se
  saisissent — aucun ne se déduit d'un autre. Les dates et heures se lisent à
  Bruxelles : la date limite est convertie en instant ici, et non laissée à
  l'interprétation du serveur.

  Le geste `jpa_enregistrer_journee` écrit la session, son détail et ses
  classes en une transaction ; ses refus sont en français et s'affichent tels
  quels.

  L'avertissement aux parents (26/10/2026) : un texte libre, facultatif, que
  les parents lisent en choisissant l'école et leurs enfants, et dans le
  courriel de confirmation. « Enregistrer et programmer le lendemain » le
  garde pour la journée suivante.
*/

interface Centre {
  id: string;
  name: string;
  tag: string;
  phone: string | null;
  school_id: string | null;
  ecole: string | null;
}

interface Formulaire {
  centreId: string;
  date: string;
  dateLimite: string;
  ouverture: string;
  prixNormal: string;
  prixReduit: string;
  prixApres: string;
  prixReduitApres: string;
  accueilDebut: string;
  accueilFin: string;
  activitesDebut: string;
  activitesFin: string;
  telephone: string;
  classes: string[];
  active: boolean;
  avertissement: string;
}

const VIDE: Formulaire = {
  centreId: '', date: '', dateLimite: '', ouverture: '',
  prixNormal: '', prixReduit: '', prixApres: '', prixReduitApres: '',
  accueilDebut: '07:30', accueilFin: '18:00', activitesDebut: '09:00', activitesFin: '15:30',
  telephone: '', classes: [], active: true, avertissement: '',
};

/** La longueur que la base accepte (`jpa_journees_avertissement_lisible`). */
const AVERTISSEMENT_MAX = 500;

const enEuros = (centimes: number) => (centimes / 100).toFixed(2).replace('.', ',');

export default function AdminJourneeFormPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [centres, setCentres] = useState<Centre[]>([]);
  const [classes, setClasses] = useState<ClasseDuCentre[]>([]);
  const [f, setF] = useState<Formulaire>(VIDE);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /* L'avertissement de la journée telle qu'elle a été lue : pour savoir s'il y en a un à retirer. */
  const [avertissementLu, setAvertissementLu] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const { data, error } = await db.from('centers')
          .select('id, name, tag, phone, school_id, archived, ecole:schools(name)')
          .eq('archived', false)
          .order('name');
        if (error) throw error;
        const lus = ((data ?? []) as (Omit<Centre, 'ecole'> & { ecole: { name: string } | { name: string }[] | null })[])
          .map((c) => ({ ...c, ecole: (Array.isArray(c.ecole) ? c.ecole[0]?.name : c.ecole?.name) ?? null }));
        setCentres(lus);
        if (sessionId) {
          const j = (await lireJournees()).find((x) => x.sessionId === sessionId);
          if (!j) throw new Error('Cette journée est introuvable.');
          setF({
            centreId: j.centreId,
            date: j.date,
            dateLimite: isoVersBruxelles(j.dateLimite),
            ouverture: j.ouverture ? isoVersBruxelles(j.ouverture) : '',
            prixNormal: enEuros(j.prixNormalCentimes),
            prixReduit: enEuros(j.prixReduitCentimes),
            prixApres: enEuros(j.prixApresLimiteCentimes),
            prixReduitApres: enEuros(j.prixReduitApresLimiteCentimes),
            accueilDebut: j.accueilDebut.slice(0, 5),
            accueilFin: j.accueilFin.slice(0, 5),
            activitesDebut: j.activitesDebut.slice(0, 5),
            activitesFin: j.activitesFin.slice(0, 5),
            telephone: j.telephoneCoordination,
            classes: j.classesAdmises,
            active: j.active,
            avertissement: j.avertissement ?? '',
          });
          setAvertissementLu(j.avertissement ?? '');
        }
      } catch (e) {
        setErreur(e instanceof Error ? e.message : String(e));
      } finally {
        setChargement(false);
      }
    })();
  }, [sessionId]);

  useEffect(() => {
    if (!f.centreId) {
      setClasses([]);
      return;
    }
    lireClasses([f.centreId], true)
      .then(setClasses)
      .catch((e) => setErreur(e instanceof Error ? e.message : String(e)));
  }, [f.centreId]);

  const centre = useMemo(() => centres.find((c) => c.id === f.centreId) ?? null, [centres, f.centreId]);
  const champ = <K extends keyof Formulaire>(k: K, v: Formulaire[K]) => setF((avant) => ({ ...avant, [k]: v }));

  const choisirDate = (date: string) => {
    setF((avant) => ({
      ...avant,
      date,
      /* La date limite suit la date tant qu'on ne l'a pas réglée à la main. */
      dateLimite: !avant.dateLimite || avant.dateLimite === dateLimiteParDefaut(avant.date)
        ? (date ? dateLimiteParDefaut(date) : '')
        : avant.dateLimite,
    }));
  };

  const choisirCentre = (id: string) => {
    const c = centres.find((x) => x.id === id);
    setF((avant) => {
      const ancien = centres.find((x) => x.id === avant.centreId);
      return { ...avant, centreId: id, classes: [], telephone: telephoneDuCentreChoisi(avant.telephone, ancien?.phone, c?.phone) };
    });
  };
  const telephoneDeLaFiche = !!centre?.phone?.trim() && f.telephone.trim() === centre.phone.trim();

  const basculerClasse = (id: string) =>
    setF((avant) => ({
      ...avant,
      classes: avant.classes.includes(id) ? avant.classes.filter((c) => c !== id) : [...avant.classes, id],
    }));

  const enregistrer = async (puisLendemain: boolean) => {
    setErreur(null);
    const prix = [f.prixNormal, f.prixReduit, f.prixApres, f.prixReduitApres].map(centimesSaisis);
    if (!f.centreId || !f.date || !f.dateLimite) {
      setErreur('Le centre, la date et la date limite sont obligatoires.');
      return;
    }
    if (prix.some((p) => p === null)) {
      setErreur('Les quatre prix sont obligatoires, en euros (par exemple 13,40).');
      return;
    }
    /* Passé le délai d'annulation, la moitié du prix reste due : elle doit tomber sur un centime. */
    if (prix.some((p) => !moitieExacte(p as number))) {
      setErreur('Chaque prix doit se couper en deux au centime près (13,40 plutôt que 13,45) : passé le délai d’annulation, la moitié reste due.');
      return;
    }
    if (f.classes.length === 0) {
      setErreur('Choisissez au moins une classe admise.');
      return;
    }
    if (!f.telephone.trim()) {
      setErreur('Le téléphone de la coordination est obligatoire : il part dans le courriel de confirmation.');
      return;
    }
    setEnCours(true);
    try {
      await enregistrerJournee({
        sessionId: sessionId ?? null,
        centreId: f.centreId,
        date: f.date,
        dateLimite: bruxellesVersIso(f.dateLimite),
        ouverture: f.ouverture ? bruxellesVersIso(f.ouverture) : null,
        prixNormalCentimes: prix[0]!,
        prixReduitCentimes: prix[1]!,
        prixApresLimiteCentimes: prix[2]!,
        prixReduitApresLimiteCentimes: prix[3]!,
        accueilDebut: f.accueilDebut,
        accueilFin: f.accueilFin,
        activitesDebut: f.activitesDebut,
        activitesFin: f.activitesFin,
        telephoneCoordination: f.telephone,
        classes: f.classes,
        active: f.active,
        /* Rien à écrire ni à retirer : on ne l'envoie pas (voir `enregistrerJournee`). */
        avertissement: f.avertissement.trim() || avertissementLu ? f.avertissement.trim() : undefined,
      });
      if (puisLendemain) {
        const lendemain = new Date(`${f.date}T12:00:00Z`);
        lendemain.setUTCDate(lendemain.getUTCDate() + 1);
        const date = lendemain.toISOString().slice(0, 10);
        setF((avant) => ({ ...avant, date, dateLimite: dateLimiteParDefaut(date) }));
        setAvertissementLu('');
        if (sessionId) navigate(CHEMINS.planification.jpaProgrammer);
      } else {
        navigate(CHEMINS.planification.jpa);
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErreur(message.includes('p_avertissement')
        ? 'L’avertissement attend la mise à jour de la base du 26/10/2026 (20261026200000). Enregistrez la journée sans avertissement, ou appliquez d’abord la mise à jour.'
        : message);
    } finally {
      setEnCours(false);
    }
  };

  if (chargement) {
    return <div className="flex justify-center items-center min-h-[40vh]"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }

  const etiquette = 'block text-sm font-semibold text-gray-700 mb-1';

  return (
    <div className="max-w-4xl space-y-5">
      <div>
        <Link to={CHEMINS.planification.jpa} className="text-sm font-semibold text-primary-700 hover:underline">← Journées pédagogiques</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">{sessionId ? 'Modifier la journée' : 'Programmer une journée'}</h1>
        <p className="text-gray-600 mt-1">Une journée par date : chacune a sa date limite.</p>
      </div>

      {erreur && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-2" role="alert">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p>{erreur}</p>
        </div>
      )}

      <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-lg font-bold">Lieu et date</h2>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="block">
            <span className={etiquette}>Centre</span>
            <select value={f.centreId} onChange={(e) => choisirCentre(e.target.value)} className="form-input">
              <option value="">Choisir…</option>
              {centres.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.tag})</option>)}
            </select>
            {centre && (
              <span className={`block text-xs mt-1 ${centre.school_id ? 'text-gray-600' : 'text-red-700'}`}>
                {centre.school_id ? `Élèves admis : ${centre.ecole}` : 'Ce centre n’est relié à aucune école. '}
                {!centre.school_id && <Link to={ficheDuCentre(centre.id, 'ecole')} className="underline font-semibold">Indiquer son école</Link>}
              </span>
            )}
          </label>
          <label className="block">
            <span className={etiquette}>Date de la journée</span>
            <input type="date" value={f.date} onChange={(e) => choisirDate(e.target.value)} className="form-input" />
          </label>
          <label className="block">
            <span className={etiquette}>Ouverture des inscriptions</span>
            <input type="datetime-local" value={f.ouverture} onChange={(e) => champ('ouverture', e.target.value)} className="form-input" />
            <span className="block text-xs text-gray-600 mt-1">Vide : ouvertes dès l’enregistrement</span>
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.active} onChange={(e) => champ('active', e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Inscriptions ouvertes aux parents (décochez pour les fermer)
        </label>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-lg font-bold">Prix et date limite</h2>
        <div className="grid gap-4 md:grid-cols-4">
          {([
            ['prixNormal', 'Prix normal (€)'],
            ['prixReduit', 'Prix réduit (€)'],
            ['prixApres', 'Après la date limite (€)'],
            ['prixReduitApres', 'Réduit après la date limite (€)'],
          ] as const).map(([k, libelle]) => (
            <label key={k} className="block">
              <span className={etiquette}>{libelle}</span>
              <input type="text" inputMode="decimal" value={f[k]} onChange={(e) => champ(k, e.target.value)} placeholder="13,40" className="form-input" />
            </label>
          ))}
        </div>
        <label className="block max-w-xs">
          <span className={etiquette}>Date limite du prix normal</span>
          <input type="datetime-local" value={f.dateLimite} onChange={(e) => champ('dateLimite', e.target.value)} className="form-input" />
        </label>
        <p className="text-sm text-gray-600">
          Proposée la veille à 23h59, heure de Bruxelles. Après, les parents peuvent encore inscrire jusqu’au jour même,
          au prix d’après. Le tarif réduit se demande par déclaration sur l’honneur ; les bons cadeaux ne valent pas pour
          les journées pédagogiques.
        </p>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-lg font-bold">Horaires et coordination</h2>
        <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
          {([
            ['accueilDebut', 'Accueil dès'],
            ['accueilFin', 'Accueil jusqu’à'],
            ['activitesDebut', 'Activités de'],
            ['activitesFin', 'Activités à'],
          ] as const).map(([k, libelle]) => (
            <label key={k} className="block">
              <span className={etiquette}>{libelle}</span>
              <input type="time" value={f[k]} onChange={(e) => champ(k, e.target.value)} className="form-input" />
            </label>
          ))}
        </div>
        <label className="block max-w-xs">
          <span className={etiquette}>Téléphone de la coordination</span>
          <input type="tel" value={f.telephone} onChange={(e) => champ('telephone', e.target.value)} className="form-input" />
          <span className="block text-xs text-gray-600 mt-1">
            {telephoneDeLaFiche && 'Repris de la fiche du centre. '}
            {centre && !centre.phone?.trim() && (
              <>
                La fiche du centre n’a pas de téléphone : <Link to={ficheDuCentre(centre.id)} className="font-semibold underline">l’y ajouter</Link>.{' '}
              </>
            )}
            Rappelé aux parents dans le courriel de confirmation.
          </span>
        </label>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div className="flex flex-wrap justify-between items-center gap-2">
          <h2 className="text-lg font-bold">Classes admises</h2>
          {centre && <Link to={ficheDuCentre(centre.id, 'classes')} className="text-sm font-semibold text-primary-700 hover:underline">Gérer les classes du centre</Link>}
        </div>
        {!centre ? (
          <p className="text-sm text-gray-600">Choisissez d’abord le centre.</p>
        ) : classes.length === 0 ? (
          <p className="text-sm text-gray-600">
            Ce centre n’a pas encore de classes. <Link to={ficheDuCentre(centre.id, 'classes')} className="font-semibold text-primary-700 underline">Les ajouter</Link>.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {classes.filter((c) => c.active || f.classes.includes(c.id)).map((c) => (
              <label key={c.id} className={`flex items-center gap-2 min-h-[44px] px-3 rounded-lg border text-sm ${f.classes.includes(c.id) ? 'border-primary-300 bg-primary-50' : 'border-gray-200'}`}>
                <input type="checkbox" checked={f.classes.includes(c.id)} onChange={() => basculerClasse(c.id)} className="h-4 w-4 rounded border-gray-300" />
                {c.libelle}
              </label>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
        <h2 className="text-lg font-bold">Avertissement aux parents</h2>
        <label className="block">
          <span className={etiquette}>Facultatif</span>
          <textarea
            value={f.avertissement}
            onChange={(e) => champ('avertissement', e.target.value)}
            maxLength={AVERTISSEMENT_MAX}
            rows={3}
            placeholder="Par exemple : les élèves de 4e, 5e et 6e primaires sont encadrés par l’ASBL BACO."
            className="form-input"
          />
          <span className="flex justify-between gap-3 text-xs text-gray-600 mt-1">
            <span>
              Les parents le lisent en choisissant l’école et leurs enfants, et dans le courriel de confirmation.
              Vide : aucun avertissement.
            </span>
            <span className="whitespace-nowrap">{f.avertissement.length}/{AVERTISSEMENT_MAX}</span>
          </span>
        </label>
      </section>

      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => void enregistrer(false)} disabled={enCours} className="btn-primary disabled:opacity-50">
          {enCours ? 'Enregistrement…' : 'Enregistrer la journée'}
        </button>
        {!sessionId && (
          <button type="button" onClick={() => void enregistrer(true)} disabled={enCours} className="btn-outline disabled:opacity-50">
            Enregistrer et programmer le lendemain
          </button>
        )}
        <Link to={CHEMINS.planification.jpa} className="btn-outline">Annuler</Link>
      </div>
    </div>
  );
}
